import { ListItem, List, Typography, Box } from '@mui/material';
import PropTypes from 'prop-types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

import THEME from '../../constants/theme';
import { navigateToPathOrSection } from '../../utils';
import { getOverallStatus } from '../../constants/serviceStatus';

const NavigationMenuItem = ({
  item,
  expandedMenu,
  handleMenuToggle,
  handleScroll,
  location,
  activeSection,
  textColor,
  activeColor,
  startPageTransition,
  navigate,
  onServiceStatusClick
}) => {
  const isHomePage = location.pathname === '/';
  const isParentActive =
    location.pathname === item.path ||
    (item.path !== '/' && location.pathname.startsWith(`${item.path}/`));
  
  // Keep dropdown open if we're on the parent page or if manually expanded
  const shouldShowDropdown = isParentActive || expandedMenu === item.label;

  // Handle Service Status item
  if (item.isServiceStatus) {
    const serviceStatus = getOverallStatus();
    
    return (
      <ListItem
        component="button"
        onClick={onServiceStatusClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 12px',
          marginLeft: '25px',
          fontSize: { md: '1rem', lg: '1.05rem', xl: '1.1rem' },
          border: 'none',
          background: 'none',
          width: '100%',
          textAlign: 'left',
          fontWeight: 300, // Explicitly set to light weight for consistency
          color: textColor,
          '&:hover': {
            color: activeColor,
            cursor: 'pointer',
            backgroundColor: 'rgba(101, 17, 175, 0.08)',
            transform: 'translateX(-2px)'
          },
          fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
          borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
          transition: 'all 0.3s ease',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: '-8px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '3px',
            height: '60%',
            backgroundColor: serviceStatus ? '#4CAF50' : '#F44336',
            borderRadius: '2px',
            opacity: 0.6
          }
        }}
      >
        <Typography
          sx={{
            textDecoration: 'none',
            color: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit'
          }}
        >
          {item.label}
        </Typography>
        
        <Box sx={{ 
          marginLeft: '10px', 
          display: 'flex', 
          alignItems: 'center',
          padding: '2px',
          borderRadius: '50%',
          backgroundColor: serviceStatus ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)'
        }}>
          {serviceStatus ? (
            <CheckCircle sx={{ 
              color: '#4CAF50', 
              fontSize: '1.1rem',
              filter: 'drop-shadow(0 1px 3px rgba(76, 175, 80, 0.4))'
            }} />
          ) : (
            <Cancel sx={{ 
              color: '#F44336', 
              fontSize: '1.1rem',
              filter: 'drop-shadow(0 1px 3px rgba(244, 67, 54, 0.4))'
            }} />
          )}
        </Box>
      </ListItem>
    );
  }

  if (item.hasDropdown) {
    return (
      <div>
        <ListItem
          sx={{
            display: 'flex',
            alignItems: 'center',          padding: '3px 8px',
          fontWeight: 300, // Explicitly set to light weight for consistency
          fontSize: { md: '1rem', lg: '1.05rem', xl: '1.1rem' },
            textAlign: 'left',
            color: isParentActive ? activeColor : textColor,
            '&:hover': {
              color: activeColor,
              cursor: 'pointer'
            },
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
          }}
        >
          {item.submenu && item.submenu.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                marginRight: '6px'
              }}
              onClick={e => {
                e.stopPropagation();
                handleMenuToggle(item.label);
              }}
            >
              <ExpandMoreIcon
                sx={{
                  transform:
                    shouldShowDropdown
                      ? 'rotate(0deg)'
                      : 'rotate(-90deg)',
                  transition: `transform ${THEME.TRANSITIONS.MEDIUM} ease`,
                  color: 'inherit',
                  fontSize: '1.1rem'
                }}
              />
            </Box>
          )}
          <Typography
            onClick={() => {
              navigateToPathOrSection(
                null,
                item.path,
                null,
                navigate,
                location.pathname,
                startPageTransition
              );
            }}
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              fontSize: 'inherit',
              cursor: 'pointer',
              fontWeight: 'inherit',
              fontFamily: 'inherit'
            }}
          >
            {item.label}
          </Typography>
        </ListItem>
        <AnimatePresence>
          {item.submenu &&
            item.submenu.length > 0 &&
            shouldShowDropdown && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                style={{ overflow: 'hidden' }}
                transition={{ duration: 0.25 }}
              >
                <List sx={{ padding: '0px 0px 0px 20px', margin: '0px 0px 0px 0px', marginTop: '-2px' }}>
                  {item.submenu.map((submenu, subIndex) => {
                    const isSubmenuActive = activeSection === submenu.id;

                    // Handle section scrolling within the same page
                    return (
                      <ListItem
                        key={subIndex}
                        component="button"
                        onClick={() => handleScroll(submenu.id, item.path)}
                        sx={{
                          cursor: 'pointer',
                          padding: '2px 8px',
                          color: isSubmenuActive
                            ? activeColor
                            : isHomePage
                              ? textColor
                              : THEME.COLORS.NEUTRAL_GRAY,
                          '&:hover': { color: activeColor },
                          fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                          marginBottom: 0,
                          border: 'none',
                          background: 'none',
                          width: '100%',
                          textAlign: 'left'
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.85rem',
                            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.LIGHT
                          }}
                        >
                          {submenu.label}
                        </Typography>
                      </ListItem>
                    );
                  })}
                </List>
              </motion.div>
            )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <ListItem
      component="button"
      onClick={() => {
        navigateToPathOrSection(
          null,
          item.path,
          null,
          navigate,
          location.pathname,
          startPageTransition
        );
      }}
      sx={{
        padding: '3px 8px',
        marginLeft: '25px',
        fontSize: { md: '1rem', lg: '1.05rem', xl: '1.1rem' },
        border: 'none',
        background: 'none',
        width: '100%',
        textAlign: 'left',
        fontWeight: 300, // Explicitly set to light weight for consistency
        color: location.pathname === item.path ? activeColor : textColor,
        '&:hover': {
          color: activeColor,
          cursor: 'pointer'
        },
        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
      }}
    >
      <Typography
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          fontSize: 'inherit',
          fontWeight: 'inherit'
        }}
      >
        {item.label}
      </Typography>
    </ListItem>
  );
};

NavigationMenuItem.propTypes = {
  item: PropTypes.object.isRequired,
  expandedMenu: PropTypes.string,
  handleMenuToggle: PropTypes.func.isRequired,
  handleScroll: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  activeSection: PropTypes.string,
  textColor: PropTypes.string.isRequired,
  activeColor: PropTypes.string.isRequired,
  startPageTransition: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  onServiceStatusClick: PropTypes.func
};

export default NavigationMenuItem;
